export type CaretSelection = { start: number; end: number }

export type CaretEditResult = { value: string; selection: CaretSelection }

type EditParams = {
  value: string
  selection: CaretSelection
  /** A single digit '0'-'9', or 'delete' for backspace. */
  key: string
  maxLength: number
  /** Strip leading zeros (used for phone numbers shown after a +62 prefix). */
  stripLeadingZeros?: boolean
}

const clamp = (n: number, min: number, max: number) =>
  Math.min(Math.max(n, min), max)

/**
 * Computes the next value + caret position for a custom on-screen numeric
 * keypad that must honour the current caret/selection instead of always
 * editing the tail of the string.
 *
 * - A digit is inserted at the caret, replacing any highlighted range.
 * - 'delete' removes the highlighted range, or the single char before the caret.
 * - The result is capped at `maxLength`.
 * - When `stripLeadingZeros` is set, leading zeros are removed and the caret is
 *   shifted left by however many stripped zeros sat before it.
 *
 * The returned `selection` is collapsed (start === end) at the new caret.
 */
export function editNumericValueAtCaret({
  value,
  selection,
  key,
  maxLength,
  stripLeadingZeros = false,
}: EditParams): CaretEditResult {
  const len = value.length
  const start = clamp(selection?.start ?? len, 0, len)
  const end = clamp(selection?.end ?? start, start, len)

  let next: string
  let caret: number

  if (key === 'delete') {
    if (start !== end) {
      // remove the highlighted range
      next = value.slice(0, start) + value.slice(end)
      caret = start
    } else if (start > 0) {
      // backspace the char before the caret
      next = value.slice(0, start - 1) + value.slice(start)
      caret = start - 1
    } else {
      // caret at the very start, nothing to delete
      return { value, selection: { start: 0, end: 0 } }
    }
  } else if (/^[0-9]$/.test(key)) {
    const lengthWithoutSelection = len - (end - start)
    if (lengthWithoutSelection >= maxLength) {
      // already at the cap, ignore the keystroke
      return { value, selection: { start, end } }
    }
    next = value.slice(0, start) + key + value.slice(end)
    caret = start + 1
  } else {
    // non-digit keypad input (e.g. period/comma), leave value untouched
    return { value, selection: { start, end } }
  }

  if (stripLeadingZeros) {
    const stripped = next.replace(/^0+/, '')
    const removed = next.length - stripped.length
    if (removed > 0) {
      caret = Math.max(0, caret - Math.min(removed, caret))
      next = stripped
    }
  }

  if (next.length > maxLength) {
    next = next.slice(0, maxLength)
    caret = Math.min(caret, maxLength)
  }

  return { value: next, selection: { start: caret, end: caret } }
}
